import React, { useEffect, useState } from "react";
import api from "../api";
import { findCountry, flagOf } from "./countries";
import { Trophy, Award, Medal, Users, RefreshCcw } from "lucide-react";

/**
 * Public Leaderboard widget — Pre-registration ecosystem ranking.
 * Two tabs: 'personal' (L1) vs 'team' (L1..L8).
 * Range selector: 10..100 step 10.
 * NO email/phone shown. Backend already strips lastName to first initial.
 */
export function PublicLeaderboard() {
  const [metric, setMetric] = useState("personal");
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/leaderboard?metric=${metric}&limit=${limit}`);
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh every 90 seconds (server cache TTL = 120s; re-poll slightly faster).
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [metric, limit]);
  useEffect(() => {
    const id = setInterval(load, 90 * 1000);
    return () => { clearInterval(id); };
    // eslint-disable-next-line
  }, [metric, limit]);

  const leaders = data?.leaders || [];
  const RANGES = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

  return (
    <section className="tg-page" style={{ padding: "40px 0 30px" }} data-testid="public-leaderboard">
      <div className="tg-card" style={{
        padding: 30,
        background: "linear-gradient(135deg, rgba(255,215,0,0.06), rgba(0,242,255,0.04) 60%, rgba(11,11,16,0.6))",
        borderColor: "rgba(255,215,0,0.25)",
      }}>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 14, marginBottom: 22 }}>
          <div>
            <div className="tg-eyebrow gold"><Trophy size={12} /> Рейтинг предрегистраций</div>
            <h2 className="tg-h2" style={{ margin: "10px 0 4px", fontSize: 26 }}>Топ лидеров до старта проекта</h2>
            <p style={{ color: "var(--tg-text-dim)", fontSize: 13, margin: 0 }}>
              Кто строит самые большие команды в экосистеме TGC прямо сейчас.{data?.updatedAt && (
                <span style={{ marginLeft: 8, opacity: 0.7 }}>
                  · обновлено {new Date(data.updatedAt).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
            </p>
          </div>
          <button onClick={load} disabled={loading} data-testid="lb-refresh"
            style={{
              padding: "8px 14px", borderRadius: 10, border: "1px solid var(--tg-border)",
              background: "transparent", color: "var(--tg-text-dim)", fontSize: 12, fontWeight: 700,
              cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
              opacity: loading ? 0.5 : 1,
            }}>
            <RefreshCcw size={12} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
            Обновить
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }} data-testid="lb-tabs">
          {[
            { id: "personal", label: "Топ по личникам", icon: <Users size={14} /> },
            { id: "team",     label: "Топ по всей команде", icon: <Award size={14} /> },
          ].map((tab) => (
            <button key={tab.id} onClick={() => setMetric(tab.id)} data-testid={`lb-tab-${tab.id}`}
              style={{
                padding: "10px 16px", borderRadius: 12, cursor: "pointer",
                border: `1px solid ${metric === tab.id ? "var(--tg-blue)" : "var(--tg-border)"}`,
                background: metric === tab.id ? "rgba(0,242,255,0.12)" : "transparent",
                color: metric === tab.id ? "var(--tg-blue)" : "var(--tg-text-dim)",
                fontWeight: 700, fontSize: 13,
                display: "flex", alignItems: "center", gap: 8,
                transition: "all .15s",
              }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Range selector */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 22 }} data-testid="lb-ranges">
          {RANGES.map((n) => (
            <button key={n} onClick={() => setLimit(n)} data-testid={`lb-range-${n}`}
              style={{
                padding: "6px 12px", borderRadius: 999, cursor: "pointer",
                border: `1px solid ${limit === n ? "#FFD700" : "var(--tg-border)"}`,
                background: limit === n ? "rgba(255,215,0,0.1)" : "transparent",
                color: limit === n ? "#FFD700" : "var(--tg-text-dim)",
                fontWeight: 700, fontSize: 12,
                transition: "all .15s",
              }}>
              Топ-{n}
            </button>
          ))}
        </div>

        {/* Leaders list */}
        {leaders.length === 0 && !loading && (
          <div style={{ padding: 40, textAlign: "center", color: "var(--tg-text-hint)" }} data-testid="lb-empty">
            Пока никто не пригласил ни одного участника. Будьте первым!
          </div>
        )}
        <div style={{ display: "grid", gap: 6 }} data-testid="lb-list">
          {leaders.map((row, idx) => (
            <LeaderRow key={idx} row={row} metric={metric} />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes lb-fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </section>
  );
}

function LeaderRow({ row, metric }) {
  const rank = row.rank;
  const isTop1 = rank === 1;
  const isTop2 = rank === 2;
  const isTop3 = rank === 3;
  const country = findCountry(row.country);
  const flag = flagOf(row.country);

  let bg = "rgba(255,255,255,0.02)";
  let borderColor = "var(--tg-border)";
  let rankColor = "var(--tg-text-hint)";
  let icon = null;
  if (isTop1) { bg = "linear-gradient(90deg, rgba(255,215,0,0.18), rgba(255,215,0,0.04))"; borderColor = "rgba(255,215,0,0.5)"; rankColor = "#FFD700"; icon = <Trophy size={16} style={{ color: "#FFD700" }} />; }
  else if (isTop2) { bg = "linear-gradient(90deg, rgba(200,200,200,0.14), rgba(200,200,200,0.03))"; borderColor = "rgba(200,200,200,0.4)"; rankColor = "#C0C0C0"; icon = <Medal size={16} style={{ color: "#C0C0C0" }} />; }
  else if (isTop3) { bg = "linear-gradient(90deg, rgba(205,127,50,0.16), rgba(205,127,50,0.03))"; borderColor = "rgba(205,127,50,0.4)"; rankColor = "#CD7F32"; icon = <Medal size={16} style={{ color: "#CD7F32" }} />; }

  return (
    <div data-testid={`lb-row-${rank}`}
      style={{
        display: "grid", gridTemplateColumns: "44px 1fr auto auto", alignItems: "center", gap: 14,
        padding: "12px 16px", borderRadius: 12,
        background: bg, border: `1px solid ${borderColor}`,
        animation: "lb-fade 0.3s ease",
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {icon}
        <span style={{ fontSize: 18, fontWeight: 900, color: rankColor }}>#{rank}</span>
      </div>
      <div style={{ fontWeight: 600, fontSize: 14, color: "var(--tg-text)" }}>
        {row.firstName} {row.lastName}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--tg-text-dim)" }}>
        <span style={{ fontSize: 18, lineHeight: 1 }}>{flag}</span>
        <span>{country.ru}</span>
      </div>
      <div style={{
        fontWeight: 900, fontSize: 16,
        color: isTop1 ? "#FFD700" : isTop2 ? "#C0C0C0" : isTop3 ? "#CD7F32" : "var(--tg-blue)",
        fontFeatureSettings: '"tnum"',
      }}>
        {row.count}
        <span style={{ fontSize: 11, marginLeft: 4, color: "var(--tg-text-hint)", fontWeight: 600 }}>
          {metric === "personal" ? "личн." : "в кмд."}
        </span>
      </div>
    </div>
  );
}
